import React, { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { API_BASE_URL, authHeaders } from "../../api";

const emptyLegal = {
    owner: {
        name: "",
        website: "",
        street: "",
        zip: "",
        city: "",
        country: "Deutschland",
        email: "",
        phone: "",
        responsiblePerson: "",
        vatId: ""
    },
    pages: {
        privacy: {
            intro: "",
            accountData: [],
            localStorageData: [],
            purpose: "",
            legalBasis: "",
            retention: "",
            deletion: "",
            sharing: "",
            rights: "",
            supervisoryAuthority: ""
        },
        imprint: {
            visible: true,
            extraInfo: "",
            disputeResolution: ""
        }
    }
};

function AdminLegal() {
    const [legal, setLegal] = useState(emptyLegal);
    const [status, setStatus] = useState("");

    useEffect(() => {
        async function loadLegal() {
            try {
                const response = await fetch(`${API_BASE_URL}/api/legal`);
                const data = await response.json();
                setLegal({
                    ...emptyLegal,
                    ...data,
                    owner: { ...emptyLegal.owner, ...data.owner },
                    pages: {
                        privacy: { ...emptyLegal.pages.privacy, ...data.pages?.privacy },
                        imprint: { ...emptyLegal.pages.imprint, ...data.pages?.imprint }
                    }
                });
            } catch (error) {
                console.error("Fehler beim Abrufen der Legal-Daten:", error);
            }
        }

        loadLegal();
    }, []);

    const updateOwner = (field, value) => {
        setLegal((current) => ({
            ...current,
            owner: {
                ...current.owner,
                [field]: value
            }
        }));
    };

    const updatePrivacy = (field, value) => {
        setLegal((current) => ({
            ...current,
            pages: {
                ...current.pages,
                privacy: {
                    ...current.pages.privacy,
                    [field]: value
                }
            }
        }));
    };

    const updateImprint = (field, value) => {
        setLegal((current) => ({
            ...current,
            pages: {
                ...current.pages,
                imprint: {
                    ...current.pages.imprint,
                    [field]: value
                }
            }
        }));
    };

    const saveLegal = async () => {
        setStatus("Speichert...");

        const payload = {
            ...legal,
            pages: {
                privacy: {
                    ...legal.pages.privacy,
                    accountData: legal.pages.privacy.accountData.filter(Boolean),
                    localStorageData: legal.pages.privacy.localStorageData.filter(Boolean)
                },
                imprint: legal.pages.imprint
            }
        };

        try {
            const response = await fetch(`${API_BASE_URL}/api/legal`, {
                method: "PUT",
                headers: authHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error("Speichern fehlgeschlagen");
            }

            setLegal(payload);
            setStatus("Gespeichert");
        } catch (error) {
            console.error(error);
            setStatus("Fehler beim Speichern");
        }
    };

    return (
        <div className="action-card">
            <h3>DSGVO & Impressum</h3>
            <div className="action-card-body">
                <div className="form-grid two-columns">
                    <label className="field-label" htmlFor="legal-name">Name</label>
                    <input id="legal-name" className="text-input" value={legal.owner.name} onChange={(event) => updateOwner("name", event.target.value)} />

                    <label className="field-label" htmlFor="legal-website">Website</label>
                    <input id="legal-website" className="text-input" value={legal.owner.website} onChange={(event) => updateOwner("website", event.target.value)} />

                    <label className="field-label" htmlFor="legal-street">Adresse</label>
                    <input id="legal-street" className="text-input" value={legal.owner.street} onChange={(event) => updateOwner("street", event.target.value)} />

                    <label className="field-label" htmlFor="legal-zip">PLZ</label>
                    <input id="legal-zip" className="text-input" value={legal.owner.zip} onChange={(event) => updateOwner("zip", event.target.value)} />

                    <label className="field-label" htmlFor="legal-city">Ort</label>
                    <input id="legal-city" className="text-input" value={legal.owner.city} onChange={(event) => updateOwner("city", event.target.value)} />

                    <label className="field-label" htmlFor="legal-country">Land</label>
                    <input id="legal-country" className="text-input" value={legal.owner.country} onChange={(event) => updateOwner("country", event.target.value)} />

                    <label className="field-label" htmlFor="legal-email">E-Mail</label>
                    <input id="legal-email" className="text-input" type="email" value={legal.owner.email} onChange={(event) => updateOwner("email", event.target.value)} />

                    <label className="field-label" htmlFor="legal-phone">Telefon</label>
                    <input id="legal-phone" className="text-input" type="tel" value={legal.owner.phone} onChange={(event) => updateOwner("phone", event.target.value)} />

                    <label className="field-label" htmlFor="legal-responsible">Verantwortlich</label>
                    <input id="legal-responsible" className="text-input" value={legal.owner.responsiblePerson} onChange={(event) => updateOwner("responsiblePerson", event.target.value)} />

                    <label className="field-label" htmlFor="legal-vat">USt-ID</label>
                    <input id="legal-vat" className="text-input" value={legal.owner.vatId} onChange={(event) => updateOwner("vatId", event.target.value)} />
                </div>

                <label className="field-label" htmlFor="privacy-intro">DSGVO Einleitung</label>
                <textarea id="privacy-intro" className="textarea" value={legal.pages.privacy.intro} onChange={(event) => updatePrivacy("intro", event.target.value)} />

                <label className="field-label" htmlFor="privacy-account">Gespeicherte Account-Daten, eine Zeile pro Eintrag</label>
                <textarea
                    id="privacy-account"
                    className="textarea"
                    value={legal.pages.privacy.accountData.join("\n")}
                    onChange={(event) => updatePrivacy("accountData", event.target.value.split("\n"))}
                />

                <label className="field-label" htmlFor="privacy-local">Local-Storage-Daten, eine Zeile pro Eintrag</label>
                <textarea
                    id="privacy-local"
                    className="textarea"
                    value={legal.pages.privacy.localStorageData.join("\n")}
                    onChange={(event) => updatePrivacy("localStorageData", event.target.value.split("\n"))}
                />

                <label className="field-label" htmlFor="privacy-purpose">Zweck der Verarbeitung</label>
                <textarea id="privacy-purpose" className="textarea" value={legal.pages.privacy.purpose} onChange={(event) => updatePrivacy("purpose", event.target.value)} />

                <label className="field-label" htmlFor="privacy-legal-basis">Rechtsgrundlage</label>
                <textarea id="privacy-legal-basis" className="textarea" value={legal.pages.privacy.legalBasis} onChange={(event) => updatePrivacy("legalBasis", event.target.value)} />

                <label className="field-label" htmlFor="privacy-retention">Speicherdauer</label>
                <textarea id="privacy-retention" className="textarea" value={legal.pages.privacy.retention} onChange={(event) => updatePrivacy("retention", event.target.value)} />

                <label className="field-label" htmlFor="privacy-deletion">Löschung</label>
                <textarea id="privacy-deletion" className="textarea" value={legal.pages.privacy.deletion} onChange={(event) => updatePrivacy("deletion", event.target.value)} />

                <label className="field-label" htmlFor="privacy-sharing">Weitergabe</label>
                <textarea id="privacy-sharing" className="textarea" value={legal.pages.privacy.sharing} onChange={(event) => updatePrivacy("sharing", event.target.value)} />

                <label className="field-label" htmlFor="privacy-rights">Betroffenenrechte</label>
                <textarea id="privacy-rights" className="textarea" value={legal.pages.privacy.rights} onChange={(event) => updatePrivacy("rights", event.target.value)} />

                <label className="field-label" htmlFor="privacy-authority">Beschwerderecht</label>
                <textarea id="privacy-authority" className="textarea" value={legal.pages.privacy.supervisoryAuthority} onChange={(event) => updatePrivacy("supervisoryAuthority", event.target.value)} />

                <label className="check-row">
                    <input
                        type="checkbox"
                        checked={legal.pages.imprint.visible !== false}
                        onChange={(event) => updateImprint("visible", event.target.checked)}
                    />
                    Impressum sichtbar
                </label>

                <label className="field-label" htmlFor="imprint-extra">Impressum Zusatztext</label>
                <textarea id="imprint-extra" className="textarea" value={legal.pages.imprint.extraInfo} onChange={(event) => updateImprint("extraInfo", event.target.value)} />

                <label className="field-label" htmlFor="imprint-dispute">Streitbeilegung</label>
                <textarea id="imprint-dispute" className="textarea" value={legal.pages.imprint.disputeResolution} onChange={(event) => updateImprint("disputeResolution", event.target.value)} />

                <div className="admin-actions">
                    <button type="button" className="btn btn-success icon-label-button" onClick={saveLegal}>
                        <Save size={18} />
                        Speichern
                    </button>
                    {status && <span className="save-status">{status}</span>}
                </div>
            </div>
        </div>
    );
}

export default AdminLegal;
